import { z } from '../zod.js';
import { AI_CONTEXTS, ASSET_KINDS, ASSET_REF_VIAS, ASSET_VARIANTS, ASSIGNABLE_CAPABILITIES, BUILTIN_TYPE_KEYS, CANON_STATUSES, CAPABILITIES, CAPTURE_KINDS, CAPTURE_STATUSES, CHANGE_LOG_OPS, CHANGE_ORIGINS, CHRON_EDGES, CHRON_RELATIONS, CLIENT_RECORD_ORIGINS, CLOCK_ADVANCE_KINDS, CLOCK_REASONS, CONSTRAINT_STATUSES, DERIVED_SOURCES, DISCOVERY_STATES, EFFECTIVE_STATUSES, FIELD_KINDS, FIELD_VALUE_SOURCES, FOUNDRY_ENTITY_KINDS, FOUNDRY_KINDS, FOUNDRY_MAP_ORIGINS, IMPORT_RESOLUTIONS, IMPORT_SOURCES, IMPORT_VIAS, INBOX_DISMISSAL_SCOPES, KNOWLEDGE_ACTIONS, LIBRARIES, LINK_ORIGINS, LINK_TYPE_KEYS, MENTION_KINDS, PARTS_OF_UNIT, PLACEMENT_KINDS, PLACEMENT_ORIGINS, PLACEMENT_STATUSES, PLAY_RECORD_STATES, PLAY_STATES, PRECISIONS, QUALIFIERS, RECORD_ORIGINS, REFERENCE_KINDS, REFERENCE_TYPE_KEYS, REVISION_REASONS, RULES_RECORD_KINDS, SESSION_CHANGE_PARTS, SESSION_ITEM_KINDS, SESSION_ORIGINS, SESSION_SOURCE_KINDS, SESSION_SOURCE_STATUSES, SESSION_STATUSES, SHEET_KINDS, SHEET_RULES_VERSIONS, SIDEBAR_ITEM_KINDS, SPAN_STATUSES, TEMPLATE_VARIANTS, TICK_REVIEW_REASONS, TICK_STATUSES, TIME_POINT_OWNER_KINDS, TIME_UNITS, TYPE_ICON_KEYS, VIOLATION_KINDS, } from './rows-enums.js';
/**
 * The shared vocabularies (1.0b deliverable 1). Each array constant is the plural of its concept
 * in capitals; its Zod schema and its inferred type share one singular PascalCase name. The values
 * live in `rows-enums.ts`; 1.0a's `CHECK` lists, the calendar engine and every server package
 * import them from here, through the default entry.
 */
export { AI_CONTEXTS, ASSET_KINDS, ASSET_REF_VIAS, ASSET_VARIANTS, ASSIGNABLE_CAPABILITIES, BUILTIN_TYPE_KEYS, CANON_STATUSES, CAPABILITIES, CAPTURE_KINDS, CAPTURE_STATUSES, CHANGE_LOG_OPS, CHANGE_ORIGINS, CHRON_EDGES, CHRON_RELATIONS, CLIENT_RECORD_ORIGINS, CLOCK_ADVANCE_KINDS, CLOCK_REASONS, CONSTRAINT_STATUSES, DERIVED_SOURCES, DISCOVERY_STATES, EFFECTIVE_STATUSES, FIELD_KINDS, FIELD_VALUE_SOURCES, FOUNDRY_ENTITY_KINDS, FOUNDRY_KINDS, FOUNDRY_MAP_ORIGINS, IMPORT_RESOLUTIONS, IMPORT_SOURCES, IMPORT_VIAS, INBOX_DISMISSAL_SCOPES, KNOWLEDGE_ACTIONS, LIBRARIES, LINK_ORIGINS, LINK_TYPE_KEYS, MENTION_KINDS, PARTS_OF_UNIT, PLACEMENT_KINDS, PLACEMENT_ORIGINS, PLACEMENT_STATUSES, PLAY_RECORD_STATES, PLAY_STATES, PRECISIONS, QUALIFIERS, RECORD_ORIGINS, REFERENCE_KINDS, REFERENCE_TYPE_KEYS, REVISION_REASONS, RULES_RECORD_KINDS, SESSION_CHANGE_PARTS, SESSION_ITEM_KINDS, SESSION_ORIGINS, SESSION_SOURCE_KINDS, SESSION_SOURCE_STATUSES, SESSION_STATUSES, SHEET_KINDS, SHEET_RULES_VERSIONS, SIDEBAR_ITEM_KINDS, SPAN_STATUSES, TEMPLATE_VARIANTS, TICK_REVIEW_REASONS, TICK_STATUSES, TIME_POINT_OWNER_KINDS, TIME_UNITS, TYPE_ICON_KEYS, VIOLATION_KINDS, };
// ---- Documents and types ----
export const Library = z.enum(LIBRARIES);
export const CanonStatus = z.enum(CANON_STATUSES);
export const AiContext = z.enum(AI_CONTEXTS);
export const Capability = z.enum(CAPABILITIES);
export const AssignableCapability = z.enum(ASSIGNABLE_CAPABILITIES);
export const FieldKind = z.enum(FIELD_KINDS);
export const FieldValueSource = z.enum(FIELD_VALUE_SOURCES);
export const FoundryKind = z.enum(FOUNDRY_KINDS);
export const TemplateVariant = z.enum(TEMPLATE_VARIANTS);
export const PlayState = z.enum(PLAY_STATES);
export const PlayRecordState = z.enum(PLAY_RECORD_STATES);
export const DiscoveryState = z.enum(DISCOVERY_STATES);
export const RevisionReason = z.enum(REVISION_REASONS);
export const ImportSource = z.enum(IMPORT_SOURCES);
export const ImportVia = z.enum(IMPORT_VIAS);
export const ImportResolution = z.enum(IMPORT_RESOLUTIONS);
export const ChangeLogOp = z.enum(CHANGE_LOG_OPS);
/** Who caused a change (the `change_log.origin` values). */
export const ChangeOrigin = z.enum(CHANGE_ORIGINS);
// ---- Assets, mentions, workspace ----
export const AssetKind = z.enum(ASSET_KINDS);
export const AssetVariant = z.enum(ASSET_VARIANTS);
export const AssetRefVia = z.enum(ASSET_REF_VIAS);
export const MentionKind = z.enum(MENTION_KINDS);
export const SidebarItemKind = z.enum(SIDEBAR_ITEM_KINDS);
// ---- Links ----
export const LinkOrigin = z.enum(LINK_ORIGINS);
export const SpanStatus = z.enum(SPAN_STATUSES);
export const InboxDismissalScope = z.enum(INBOX_DISMISSAL_SCOPES);
// ---- Chronology ----
export const ChronEdge = z.enum(CHRON_EDGES);
export const TimePointOwnerKind = z.enum(TIME_POINT_OWNER_KINDS);
export const Precision = z.enum(PRECISIONS);
export const Qualifier = z.enum(QUALIFIERS);
export const TickStatus = z.enum(TICK_STATUSES);
export const PartOfUnit = z.enum(PARTS_OF_UNIT);
export const TickReviewReason = z.enum(TICK_REVIEW_REASONS);
export const PlacementKind = z.enum(PLACEMENT_KINDS);
export const PlacementStatus = z.enum(PLACEMENT_STATUSES);
export const EffectiveStatus = z.enum(EFFECTIVE_STATUSES);
export const PlacementOrigin = z.enum(PLACEMENT_ORIGINS);
export const ChronRelation = z.enum(CHRON_RELATIONS);
export const ConstraintStatus = z.enum(CONSTRAINT_STATUSES);
export const ViolationKind = z.enum(VIOLATION_KINDS);
export const TimeUnit = z.enum(TIME_UNITS);
export const ClockAdvanceKind = z.enum(CLOCK_ADVANCE_KINDS);
export const ClockReason = z.enum(CLOCK_REASONS);
// ---- Play, discovery, sessions ----
export const KnowledgeAction = z.enum(KNOWLEDGE_ACTIONS);
export const RecordOrigin = z.enum(RECORD_ORIGINS);
export const SessionStatus = z.enum(SESSION_STATUSES);
export const SessionOrigin = z.enum(SESSION_ORIGINS);
export const CaptureKind = z.enum(CAPTURE_KINDS);
export const CaptureStatus = z.enum(CAPTURE_STATUSES);
export const ClientRecordOrigin = z.enum(CLIENT_RECORD_ORIGINS);
export const SessionItemKind = z.enum(SESSION_ITEM_KINDS);
export const SessionSourceKind = z.enum(SESSION_SOURCE_KINDS);
export const SessionSourceStatus = z.enum(SESSION_SOURCE_STATUSES);
export const SessionChangePart = z.enum(SESSION_CHANGE_PARTS);
// ---- 5e, reference library, Foundry ----
export const SheetKind = z.enum(SHEET_KINDS);
export const SheetRulesVersion = z.enum(SHEET_RULES_VERSIONS);
export const DerivedSource = z.enum(DERIVED_SOURCES);
export const RulesRecordKind = z.enum(RULES_RECORD_KINDS);
export const ReferenceKind = z.enum(REFERENCE_KINDS);
export const FoundryEntityKind = z.enum(FOUNDRY_ENTITY_KINDS);
export const FoundryMapOrigin = z.enum(FOUNDRY_MAP_ORIGINS);
// ---- Built-in keys ----
export const BuiltinTypeKey = z.enum(BUILTIN_TYPE_KEYS);
export const ReferenceTypeKey = z.enum(REFERENCE_TYPE_KEYS);
/** An icon the server accepts for a type (CS r2): one of `TYPE_ICON_KEYS`. */
export const TypeIconKey = z.enum(TYPE_ICON_KEYS);
/**
 * A built-in link-type key. The GM may create link types of his own, whose keys this enum does
 * not hold: a link type's own key is a plain string in its DTO.
 */
export const LinkTypeKey = z.enum(LINK_TYPE_KEYS);
// ---- Ticks and the size cap ----
/**
 * The tick bounds: the `tick` domain of baseline section 3 (`bigint` between these values),
 * ±(2^53 − 1), so a tick travels as a plain JSON number without loss.
 */
export const TICK_MIN = -9007199254740991;
export const TICK_MAX = 9007199254740991;
/** True for an integer within [{@link TICK_MIN}, {@link TICK_MAX}]. */
export function isTick(value) {
    return (typeof value === 'number' && Number.isInteger(value) && value >= TICK_MIN && value <= TICK_MAX);
}
/** A tick on the wire: signed seconds from the calendar epoch, a JSON number within the bounds. */
export const Tick = z.number().int().min(TICK_MIN).max(TICK_MAX);
/**
 * The word count past which a document gets the nudge to split (PLAN section 2, "Long
 * documents"; DECISIONS entry 29). The value is a setting; this is its default.
 */
export const SIZE_CAP_WORDS_DEFAULT = 20_000;
//# sourceMappingURL=enums.js.map